// The Assistant's Help article (REQ-118, SCR-310/311): what it can and cannot do, conversations
// and their states, who pays, and a section per refusal code the screens link to.
import { Callout, ClaudeCodeCommand, HelpLink, Related } from "./parts";

export function Assistant() {
  return (
    <>
      <p>
        The <strong>Assistant</strong> tab on a customer's page answers questions about that customer in plain words: who
        used the most last week, how the quotas stand, which models are on by default, which teams exist. It reads the same
        reports you can open yourself and names people and teams by name or email, never by an id.
      </p>

      <h2 id="what-it-can-do">What it can and cannot do</h2>
      <ul>
        <li>It reads the customer's usage reports, quotas and quota standing, people, teams, installations and installation settings.</li>
        <li>It reads only what you may read: it acts as you, with your role, and every read is checked the same way a screen's is.</li>
        <li>
          It changes nothing on its own: it proposes a change, and a person approves it (see{" "}
          <HelpLink to="assistant-approvals" />).
        </li>
        <li>It never sees prompt bodies, secrets or keys, and it treats names and text from the customer as data, never as instructions.</li>
      </ul>

      <h2 id="conversations">Conversations</h2>
      <p>
        Choose <strong>New conversation</strong>, or one of the suggested questions, and type. Enter sends; Shift+Enter
        adds a line. While the Assistant works the composer is disabled and each report it reads shows as one line in the
        transcript.
      </p>
      <ul>
        <li>
          <strong>Paused</strong>: the Assistant waits to hear from you again before it reads anything more, so it always
          acts with your current sign-in. Send a message to continue.
        </li>
        <li>
          <strong>Ended</strong>: after 30 minutes without activity, after 24 hours, when its model budget is spent, when the
          model is unavailable, or when you choose End conversation. An ended conversation can still be read.
        </li>
      </ul>

      <h2 id="claude-code">Use the Assistant from Claude Code</h2>
      <p>
        You can ask the Assistant from your own Claude Code instead of this site. Add this site to Claude Code once, in a
        terminal (53682 is any free port on your machine for the sign-in to come back to):
      </p>
      <ClaudeCodeCommand />
      <ol>
        <li>
          In Claude Code, run <code>/mcp</code>, pick <code>nexus</code> and choose <strong>Authenticate</strong>.
        </li>
        <li>Your browser opens this site's sign-in: sign in. Already signed in, you go straight to the next step.</li>
        <li>
          A consent page names this site, you and the client <code>nexus</code>: click <strong>Allow</strong>. The
          Assistant's tools appear in Claude Code.
        </li>
      </ol>
      <p>
        From Claude Code the Assistant can read and propose, as it does here, naming the customer you ask about in each
        request, but it never applies a change: it gives you a link, and you approve or reject the change in your browser at
        that link. The sign-in lasts one hour, and ends sooner when your sign-in to this site ends; then run{" "}
        <code>/mcp</code> and choose <strong>Authenticate</strong> again.
      </p>
      <p>
        A conversation labelled <strong>Claude Code</strong> is driven from your own Claude Code, so it has no message box
        here. Its proposed changes are approved or rejected on that conversation, as on any other.
      </p>

      <h2 id="who-pays">Who pays</h2>
      <p>
        The Assistant on this site runs on AllCode's own Bedrock account: the customer is never billed for it. The model
        spend of each conversation is shown beside it, and the usage ledger never counts it. Each customer has a daily
        Assistant budget, and so does the whole fleet.
      </p>

      <h2 id="refusals">When it refuses</h2>
      <p>A refusal names its reason and links here.</p>

      <h3 id="agent-disabled">The Assistant is turned off</h3>
      <p>
        The Assistant is switched off for the fleet, or not offered for this customer. Nothing runs and no conversation can
        be opened. Ask an operator who manages the deployment to turn it on.
      </p>

      <h3 id="session-terminal">This conversation has ended</h3>
      <p>
        The conversation ended (see <HelpLink to="assistant" section="conversations">Conversations</HelpLink>), so it
        takes no more messages. Open <strong>All conversations</strong> and start a new one.
      </p>

      <h3 id="agent-daily-budget-exhausted">Today's Assistant budget is spent</h3>
      <p>
        The customer's, or the fleet's, daily Assistant budget is used up, so no new turn starts. The message says when to
        try again; the budget resets daily.
      </p>
      <Callout>A spent budget never touches the customer's own quotas or usage: it is AllCode's spend only.</Callout>

      <Related to={["quotas-and-notifications", "users-teams-and-sign-in", "audit-log"]} />
    </>
  );
}
